import { StorageLevel } from '../common/configs.models';

export interface DateRangeIsoStr {
  dateFrom: string;
  dateTo: string;
}

export interface DateQueryParams {
  date: string;
}

export interface HierarchyNode {
  id: string;
  name: string;
  parent: string | null;
  parentId?: string | null;
  additionalParents?: string[] | null;
  levelBk: string;
}

export type ComponentsPerLevelCount = {
  [key in StorageLevel]?: number;
};

export class ComponentV2 {
  componentBk: string;
  parentComponentBks?: string[];
  name: string;
  levelBk: StorageLevel;
}

export interface ComponentTreeResponseV2 {
  electricalHierarchy: ComponentV2[];
  physicalHierarchy: ComponentV2[];
}

export enum ComponentStatus {
  HighAlert = 'HIGH_ALERT',
  MediumAlert = 'MEDIUM_ALERT',
  LowAlert = 'LOW_ALERT',
  Offline = 'OFFLINE',
  Normal = 'NORMAL',
}

export enum HierarchyType {
  ELECTRICAL = 'ELECTRICAL',
  PHYSICAL = 'PHYSICAL',
}

export interface AlertCounts {
  [ComponentStatus.LowAlert]: number;
  [ComponentStatus.MediumAlert]: number;
  [ComponentStatus.HighAlert]: number;
  [ComponentStatus.Offline]: number;
}

export interface Edge {
  source: { x: number; y: number };
  target: { x: number; y: number };
  midY: number;
}

export interface D3Edge extends Edge {
  path: string;
  id: string;
}

export interface BoundingRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export const statusPriority: Record<ComponentStatus, number> = {
  [ComponentStatus.HighAlert]: 5,
  [ComponentStatus.MediumAlert]: 4,
  [ComponentStatus.LowAlert]: 3,
  [ComponentStatus.Offline]: 2,
  [ComponentStatus.Normal]: 1,
};

export enum SldMode {
  Analytics = 'ANALYTICS',
  Live = 'LIVE',
}

export enum ComponentOperatingState {
  Charging = 'CHARGING',
  Discharging = 'DISCHARGING',
  Idle = 'IDLE',
}

export interface ComponentOperatingStateClassification {
  state: ComponentOperatingState;
  timestamp: string;
  basedOn: string;
}

export interface KpiValue {
  value: number;
  timestamp: string;
  isSensorReading: boolean;
  insertTimestamp: string | null;
  standardSensorBk: string | null;
}

interface StorageComponentSummary {
  componentType: 'STORAGE';
  activePower: KpiValue | null;
  socPct: KpiValue | null;
  temperature: KpiValue | null;
  operatingState: ComponentOperatingStateClassification | null;
}

interface BatteryComponentSummary {
  componentType: 'BATTERY';
  current: KpiValue | null;
  temperature: KpiValue | null;
  socPct: KpiValue | null;
  voltage: KpiValue | null;
  power: KpiValue | null;
  operatingState: ComponentOperatingStateClassification | null;
}

interface PowerConversionComponentSummary {
  componentType: 'POWER_CONVERSION';
  activePower: KpiValue | null;
  powerDc: KpiValue | null;
  operatingState: ComponentOperatingStateClassification | null;
}

export type ComponentSummary = StorageComponentSummary | BatteryComponentSummary | PowerConversionComponentSummary;

export interface StorageLiveKpiSummaryResponse {
  componentSummaries: Record<string, ComponentSummary>;
}

export interface LiveKpiInfo {
  label: string;
  value: string;
  timestamp: string;
  isSensorReading: boolean;
  standardSensorBk: string | null;
}

// region Analytics KPIs
export interface SohStateSummary {
  lastSohPct: number;
  standardSensorBk: string | null;
}

export interface ThroughputSummary {
  dateRange: DateRangeIsoStr;
  totalCycles: number | null;
  avgDailyCycles: number | null;
  totalEnergyThroughputWh: number;
  avgEnergyThroughputWh: number;
  cyclesStandardSensorBk: string | null;
  throughputStandardSensorBk: string | null;
}

export interface EnergyBreakdownSummary {
  usableEnergyWh: number;
  recoverableEnergyWh: number;
  missingDataEnergyWh: number;
  energyLossAgingWh: number;
}

export interface AvailabilitySummary {
  dateRange: DateRangeIsoStr;
  availabilityPct: number;
  standardSensorBk: string | null;
}

export interface ComponentAnalyticsKpiSummary {
  soh: SohStateSummary | null;
  throughput: ThroughputSummary | null;
  usableEnergy: EnergyBreakdownSummary | null;
  availability: AvailabilitySummary | null;
}

export interface StorageAnalyticsKpiSummary {
  componentSummaries: Record<string, ComponentAnalyticsKpiSummary>;
}

export enum NoAlertsStatus {
  Normal,
  HasLowAvailability,
  HasHighMissingDataForRecoverableEnergy,
}

export interface StorageHierarchyAlert {
  componentBk: string;
  status: ComponentStatus;
  isOngoing: boolean;
}

// endregion
export interface AlertsStatus {
  alertsStatus: StorageHierarchyAlert[];
}
